// El runtime del widget: lo que en la home hacen las 2.486 líneas de
// portadaRenfeViajes#buscadorDestinos.js, sin jQuery, typeahead, Bloodhound, moment,
// bootstrap-datepicker, Litepicker ni SweetAlert2.
//
// Aquí está el esqueleto: el estado de cada instancia, las pestañas y los campos de estación.
// El calendario y los pasajeros viven en sus propios ficheros porque son los dos trozos con
// reglas propias; las salidas (las 6 formas de URL) son F3.
//
// Un detalle de comportamiento que hay que respetar y que no se ve en el markup: las pestañas
// "Hotel" y "Tren + Coche de alquiler" NO son pestañas. Abren una ventana nueva y devuelven
// false, así que la pestaña marcada sigue siendo la que estaba. Si se tratan como las demás,
// el cliente se queda mirando un bloque vacío.
import { montar as montarDom, PESTANAS, seleccionarPestana, limitarPestanas, ocultarBarraDePestanas } from './montar.js'
import { autocompletar } from './autocompletado.js'
import { crearCacheEstaciones } from './estaciones.js'
import { abrirPanelMovil, cerrarPanelMovil } from './panel-movil.js'
import { Datos } from './datos.js'
import { textosPorDefecto } from './textos.js'
import { crearCalendario } from './calendario.js'
import { crearPasajeros } from './pasajeros.js'
import { crearOcio } from './ocio.js'
import { crearBusqueda } from './busqueda.js'
import { aviso } from './modales.js'
import { recordar, aplicar } from './atribucion.js'

// A dónde saltan las dos pestañas que no son buscador.
const BOOKING = 'https://www.booking.com/?aid=1649928'

/**
 * El destino de una pestaña que la landing ha convertido en enlace (`data-enlace-hotel`,
 * `data-enlace-ocio`), ya resuelto, o null si no vale.
 *
 * `{lang}` se cambia por el idioma del buscador. Una ruta que empieza por `/` cuelga del
 * dominio del servicio, igual que el resto de salidas del widget, para que una landing de otro
 * dominio no acabe en una página suya. Sólo se aceptan http(s) y rutas: el valor viene de la
 * página de un tercero y acaba en `window.open`/`location`, así que un `javascript:` no puede
 * colarse.
 *
 * @param {string|undefined} valor
 * @param {string} locale
 * @param {string} origen
 * @returns {string|null}
 */
export function resolverEnlace (valor, locale, origen) {
  const limpio = String(valor || '').trim().split('{lang}').join(locale || 'es')
  if (limpio.startsWith('/') && !limpio.startsWith('//')) return origen + limpio
  if (/^https?:\/\//i.test(limpio)) return limpio

  return null
}

/**
 * Los buscadores de UN SOLO PRODUCTO (`data-producto`), los que abren los botones de las
 * landings en un modal. Los dos son el bloque de tren de siempre —las mismas estaciones, el
 * mismo calendario, los mismos pasajeros— y lo que cambia es la conversación de después de
 * pulsar Buscar (ver busqueda.js):
 *
 *   tren-hotel  el «¿Quieres añadir hotel?» ya contestado que sí: se va directo a las noches
 *               y los pasajeros, y sale a Compra/3833.
 *   tren-coche  el de la página de coches (la 656): pregunta el hotel igual, y según la
 *               respuesta sale a Compra/5997 (tren + hotel + coche) o a Compra/5996 (tren +
 *               coche). Los pasajeros llevan la edad del conductor, y el DESTINO sale de otro
 *               catálogo: sólo las estaciones con oficina de alquiler.
 */
const PRODUCTOS = {
  'tren-hotel': 'tren-hotel',
  trenhotel: 'tren-hotel',
  'tren+hotel': 'tren-hotel',
  'tren-coche': 'tren-coche',
  trencoche: 'tren-coche',
  'tren+coche': 'tren-coche',
}

/** El producto pedido, o null si no se pide ninguno (el buscador de siempre). */
export function productoPedido (valor) {
  const limpio = String(valor || '').trim().toLowerCase().replace(/\s+/g, '')
  if (!limpio) return null
  if (PRODUCTOS[limpio]) return PRODUCTOS[limpio]
  console.warn('[buscador] data-producto no reconocido: ' + valor + ' (se monta el buscador completo)')
  return null
}

/** El bloque en el que viven los dos buscadores de un solo producto. */
const BLOQUE_PRODUCTO = 'bloquePaqueteTren'

/**
 * Los campos de estación de cada bloque. Son los mismos nombres de clase que en la home, ya
 * prefijados: el origen es `station1Input` y el destino `station2Input`.
 */
const CAMPOS = {
  origen: '.rvb-station1Input',
  destino: '.rvb-station2Input',
}

export class Buscador {
  /**
   * @param {object} opciones
   * @param {Element|string} opciones.destino  dónde se monta
   * @param {string} [opciones.locale]
   * @param {string|number} [opciones.marca]
   * @param {string} [opciones.origen]     el dominio del que cuelgan las llamadas y las salidas
   * @param {string} [opciones.pestana]
   * @param {boolean} [opciones.portada]  el buscador de la home, que flota y no ocupa sitio
   * @param {object} [opciones.textos]
   * @param {string} [opciones.estacionOrigen]   estación de salida ya puesta (código, id o nombre)
   * @param {string} [opciones.estacionDestino]  ídem para la de llegada
   * @param {boolean} [opciones.sinPestanas]     esconde la barra de pestañas entera
   * @param {boolean} [opciones.desplegado]      el buscador entero a la vista desde el principio
   * @param {boolean} [opciones.origenFijo]      la de salida, además, no se puede cambiar
   * @param {boolean} [opciones.destinoFijo]     ídem
   * @param {string} [opciones.producto]         `tren-hotel` o `tren-coche`: un buscador de un
   *                                             solo producto, sin pestañas (ver PRODUCTOS)
   * @param {string} [opciones.enlaceHotel]      la pestaña Hotel abre esta URL en vez de Booking
   * @param {string} [opciones.enlaceOcio]       la pestaña Ocio lleva a esta página en vez de
   *                                             desplegar su buscador; admite `{lang}`
   */
  constructor (opciones = {}) {
    // Un buscador de un solo producto es el bloque de tren sin barra de pestañas, diga lo que
    // diga el resto de la configuración: se fija aquí, antes de montar, porque el montaje ya
    // abre la pestaña pedida.
    this.producto = productoPedido(opciones.producto)
    if (this.producto) {
      opciones = Object.assign({}, opciones, {
        pestana: 'paquetetren',
        pestanas: 'paquetetren',
        sinPestanas: true,
      })
    }
    this.opciones = opciones
    this.datos = new Datos(opciones)
    // Las pestañas que la landing convierte en enlace. Sin `data-enlace-*`, las de siempre.
    this.enlaces = {
      hotel: resolverEnlace(opciones.enlaceHotel, opciones.locale, this.datos.origen),
      actividades: resolverEnlace(opciones.enlaceOcio, opciones.locale, this.datos.origen),
    }
    this.dom = montarDom(opciones.destino, opciones)
    if (!this.dom) return

    this.raiz = this.dom.raiz
    this.overlays = this.dom.overlays
    // Se lee una sola vez, al montar: la URL de la landing puede cambiar después (un router
    // de la propia landing, por ejemplo) y la campaña con la que entró el cliente es la que
    // vale.
    this.atribucion = recordar()
    // Un estado por bloque: cada pestaña tiene sus propias estaciones, fechas y pasajeros, y
    // en la home eso es así de hecho — cambiar de pestaña no arrastra lo que llevabas.
    this.estado = {}
    this.piezas = []          // lo que haya que desmontar al destruir
    // La caché de estaciones, compartida por los dos bloques que las piden. Se crea antes de
    // cablear nada porque los campos se enganchan a ella al nacer.
    this.estaciones = crearCacheEstaciones({
      datos: this.datos,
      marca: opciones.marca,
      locale: opciones.locale,
    })
    if (this.producto) this.raiz.setAttribute('data-producto', this.producto)
    // Y la del destino del tren + coche, que es OTRA lista: sólo las estaciones con oficina de
    // alquiler. El origen sigue saliendo de la de siempre.
    if (this.producto === 'tren-coche') {
      this.estacionesCoche = crearCacheEstaciones({
        datos: this.datos,
        marca: opciones.marca,
        locale: opciones.locale,
        tipo: 'trenCoche',
      })
    }

    this.cablearPestanas()
    PESTANAS.filter((p) => p.bloque).forEach((p) => this.cablearBloque(p.bloque))

    // Las pestañas que la landing quiere a la vista. Las demás se esconden, pero se dejan
    // cableadas: así `data-tab` o la API siguen pudiendo abrir una escondida si hace falta.
    this.pestanasVisibles = opciones.pestanas
      ? limitarPestanas(this.raiz, opciones.pestanas)
      : PESTANAS.map((p) => p.clave)

    // Y la barra entera, que una landing puede no querer: el buscador se queda con un solo
    // bloque, el que diga `data-tab`.
    if (opciones.sinPestanas) ocultarBarraDePestanas(this.raiz)

    // La que se abre al montar: la pedida, si está a la vista; si no, la primera visible. Sin
    // esto, pedir `data-pestanas="ocio"` dejaba abierto un bloque cuya pestaña ya no se ve.
    // Una pestaña convertida en enlace no se abre nunca: no tiene bloque que enseñar.
    const abrible = (c) => !this.enlaces[c] && PESTANAS.find((p) => p.clave === c && p.bloque)
    this.pestanaActual = (opciones.pestana && this.pestanasVisibles.includes(opciones.pestana)
      && !this.enlaces[opciones.pestana])
      ? opciones.pestana
      : (this.pestanasVisibles.find(abrible)
        || this.pestanasVisibles[0]
        || 'paquetetren')
    seleccionarPestana(this.raiz, this.pestanaActual)
    // El catálogo de Ocio se pide AL CARGAR, como en la home, y no al abrir la pestaña.
    //
    // Esperar a que alguien entre en Ocio ahorraba una petición de cientos de filas a quien no
    // la va a usar, pero dejaba una diferencia visible: la pestaña se abría con el spinner
    // puesto un instante, mientras que en el buscador de siempre el campo ya está listo. Y lo
    // que se busca es que no se note ninguna diferencia.
    // Con la pestaña Ocio convertida en enlace no hace falta: nadie va a escribir en ella.
    if (this.ocio && !this.enlaces.actividades) this.ocio.catalogoListo()

    // El catálogo de estaciones, en segundo plano y cuando el navegador esté ocioso. Es UNA
    // petición (`Search/stationsRenfeAll`), cacheada en el servidor y en el navegador, y con
    // ella el autocompletado deja de consultar: por eso va encendida por defecto.
    //
    // Si el servidor todavía no tiene ese endpoint —una preproducción sin desplegar, por
    // ejemplo— no pasa nada: se avisa por consola y el widget sigue con la caché por consulta,
    // que no cuesta ninguna petición de más. Lo que NO hace es caer a pedir letra por letra.
    //
    // `data-precargar-estaciones="no"` la apaga, para una landing que prefiera no gastar esa
    // petición.
    if (false !== opciones.precargarEstaciones) {
      this.estaciones.precargarCuandoHaya()
      if (this.estacionesCoche) this.estacionesCoche.precargarCuandoHaya()
    }

    // El buscador de la home arranca EN CORTO: sólo los dos campos de estación, y las fechas,
    // los pasajeros y el botón aparecen cuando se toca uno. En una landing eso suele estorbar
    // --el cliente ve medio buscador y no sabe que hay más-- así que se puede pedir entero
    // desde el principio. Se hace con la misma clase que usa el propio buscador al enfocar, no
    // con CSS nuevo.
    if (opciones.desplegado) this.desplegar()

    // Y el origen y el destino que venga puesto la landing.
    this.ponerEstacionesDeSalida()
  }

  /**
   * Deja puestos el origen y el destino que pida la landing (`data-origen`, `data-destino`).
   *
   * No basta con escribir el nombre en el campo: lo que hace falta es la FILA ENTERA que
   * devuelve el servidor, porque de su `id` y su `url` dependen las URLs de salida. Por eso se
   * resuelve contra la caché o contra el servidor, y si no se encuentra la estación no se
   * escribe nada — un campo con texto pero sin estación resuelta es justo el caso que deja la
   * búsqueda sin `id`, y el buscador lo marca en rojo.
   *
   * Se aplica a TODOS los bloques que tengan campos de estación, no sólo al que esté abierto:
   * cambiar de pestaña no debería perder lo que la landing dejó puesto.
   */
  async ponerEstacionesDeSalida () {
    const deseados = [
      ['origen', this.opciones.estacionOrigen, this.opciones.origenFijo],
      ['destino', this.opciones.estacionDestino, this.opciones.destinoFijo],
    ].filter(([, valor]) => valor)
    if (!deseados.length || !this.campos) return

    for (const [cual, valor, fijo] of deseados) {
      // Cada campo se resuelve contra SU catálogo: el destino del tren + coche no es la lista
      // general, y una estación sin coches no puede quedar puesta ahí.
      const resueltas = new Map()
      for (const clave of Object.keys(this.campos)) {
        const pieza = this.campos[clave]
        if (pieza.cual !== cual) continue
        if (!resueltas.has(pieza.catalogo)) {
          let encontrada = null
          try { encontrada = await pieza.catalogo.resolver(valor) } catch (e) { encontrada = null }
          if (!encontrada) console.warn('[buscador] no encuentro la estación "' + valor + '"')
          resueltas.set(pieza.catalogo, encontrada)
        }
        const estacion = resueltas.get(pieza.catalogo)
        if (!estacion) continue
        pieza.ac.fijar(estacion)
        pieza.estado[cual] = estacion
        if (pieza.contenedor) pieza.contenedor.classList.remove('rvb-error')
        if (fijo) this.bloquearCampo(pieza)
        this.revisar(clave.split('.')[0])
      }
    }
  }

  /**
   * Un campo que la landing ha dejado FIJO: se ve igual, pero no se puede escribir en él ni
   * sale el desplegable. `readonly` y no `disabled` a propósito — un campo deshabilitado no
   * recibe foco, se lee distinto y en un formulario no se envía.
   */
  bloquearCampo (pieza) {
    pieza.campo.setAttribute('readonly', 'readonly')
    pieza.campo.setAttribute('aria-readonly', 'true')
    pieza.campo.classList.add('rvb-fijo')
    pieza.ac.cerrar()
    // El autocompletado se queda ahí pero sin nada que hacer: sin eventos no abre la lista.
    pieza.ac.destruir()
  }

  /**
   * El catálogo del que sale un campo de estación. Todos usan el general salvo el destino del
   * tren + coche.
   */
  catalogoDe (bloque, cual) {
    if (this.estacionesCoche && bloque === BLOQUE_PRODUCTO && cual === 'destino') return this.estacionesCoche
    return this.estaciones
  }

  /** Deja el buscador entero a la vista, como si ya se hubiera tocado un campo. */
  desplegar () {
    this.raiz.querySelectorAll('.rvb-bloque-estaciones').forEach((b) => b.classList.add('rvb-open'))
  }

  /** El estado de un bloque, creándolo la primera vez. */
  estadoDe (bloque) {
    if (!this.estado[bloque]) {
      this.estado[bloque] = {
        origen: null,        // la fila ENTERA de Search/stationsRenfe, no sólo el nombre
        destino: null,
        fechaIda: null,
        fechaVuelta: null,
        soloIda: false,
        pasajeros: null,
      }
    }
    return this.estado[bloque]
  }

  nodo (bloque, selector) {
    const dentro = '[data-rvb="' + bloque + '"] ' + selector
    // Y si no está en el widget, en los overlays: mientras el calendario está abierto en una
    // landing que lo recortaba, su capa vive ahí fuera (ver portal.js). Sin este respaldo, el
    // radio de "sólo ida" no encontraría el calendario justo cuando está a la vista.
    return this.raiz.querySelector(dentro)
      || (this.overlays ? this.overlays.querySelector(dentro) : null)
  }

  /**
   * Un texto, por id de la tabla `traducciones` o por la clave de los que la home escondía en
   * el DOM (`texto-aviso-titulo-swal`, `texto_habitaciones`…). Lo que falte cae al castellano
   * de la plantilla: en la landing de un tercero, un texto que no esté no puede tumbar nada.
   */
  texto (clave) {
    const propios = this.opciones.textos || {}
    if (propios[clave] !== undefined) return propios[clave]
    const plurales = this.dom.plurales || {}
    if (plurales[clave] !== undefined) {
      // El diccionario de plurales guarda ids de traducción, no textos.
      const v = plurales[clave]
      return typeof v === 'number' ? this.texto(v) : v
    }
    return textosPorDefecto[clave] !== undefined ? textosPorDefecto[clave] : ''
  }

  // --- pestañas ----------------------------------------------------------------------
  cablearPestanas () {
    PESTANAS.forEach((pestana) => {
      const boton = this.raiz.querySelector('[data-rvb="' + pestana.marca + '"]')
      if (!boton) return
      // Son <div>, no botones: sin esto no hay forma de llegar con el teclado.
      boton.setAttribute('role', 'tab')
      boton.setAttribute('tabindex', '0')
      const abrir = (e) => {
        if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return
        e.preventDefault()
        this.abrirPestana(pestana)
      }
      boton.addEventListener('click', abrir)
      boton.addEventListener('keydown', abrir)
    })
  }

  abrirPestana (pestana) {
    // La pestaña que la landing ha convertido en enlace: Hotel sale a otra web, en otra
    // pestaña del navegador, como Booking; Ocio es una página del propio sitio y va en esta.
    const enlace = this.enlaces[pestana.clave]
    if (enlace) {
      this.analitica(pestana.clave === 'hotel' ? 'buscador_hoteles' : 'buscador_ocio')
      if (pestana.clave === 'hotel') {
        window.open(enlace, '_blank', 'noopener')
      } else {
        window.location.assign(enlace)
      }
      return
    }
    if (!pestana.bloque) {
      // Hotel y coche de alquiler: se van fuera y la pestaña marcada no cambia.
      const evento = pestana.clave === 'hotel' ? 'buscador_hoteles' : 'buscador_coches'
      this.analitica(evento)
      const url = pestana.clave === 'hotel' ? BOOKING : this.datos.url('coches')
      window.open(url, '_blank', 'noopener')
      return
    }
    this.pestanaActual = pestana.clave
    seleccionarPestana(this.raiz, pestana.clave)
    // El campo de la pestaña Ocio vive DETRÁS del loader: mientras no llegue el catálogo no
    // hay dónde escribir. Por eso se pide al abrir la pestaña, no a la primera letra.
    if (pestana.clave === 'actividades' && this.ocio) this.ocio.catalogoListo()
  }

  /**
   * Avisa a la analítica, si la hay. En una landing ajena puede no haber ni `dataLayer` ni
   * `_satellite`, y llamar a lo que no existe tumbaría el buscador entero.
   */
  analitica (evento, datos = {}) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: evento }, datos))
      }
    } catch (e) { /* la analítica nunca puede tumbar el buscador */ }
  }

  /**
   * Un aviso de una línea. La home usa `alert()`, que bloquea la pestaña entera de la landing
   * anfitriona: aquí va por el modal propio.
   */
  avisar (texto) {
    return aviso(this, {
      titulo: this.texto('texto-aviso-titulo-swal'),
      texto,
      confirmar: this.texto('texto-aviso-confirmar-swal'),
    })
  }

  /** Lo mismo para Adobe Launch, que es el otro canal que usa la home. */
  analiticaAdobe (evento, datos = {}) {
    try {
      if (window._satellite && typeof window._satellite.track === 'function') {
        window._satellite.track(evento, datos)
      }
    } catch (e) { /* ídem */ }
  }

  /**
   * LA ÚNICA SALIDA del widget. Todo lo que navega pasa por aquí, y no por un
   * `location.href` suelto: así hay un solo sitio al que añadir la atribución (los utm, el
   * gclid y compañía) en F3, y un solo sitio que mirar cuando una URL salga mal.
   * @param {string} url  absoluta
   */
  /**
   * Enseña el velo de "cargando". Se crea en ese momento, no antes: un elemento a pantalla
   * completa que depende del CSS para esconderse es una forma segura de tapar la landing si
   * la hoja tarda en llegar.
   */
  cargando () {
    if (this._velo) return this._velo
    const velo = this.dom.plantilla('cargando')
    if (!velo) return null
    this.overlays.appendChild(velo)
    this._velo = velo
    return velo
  }

  irA (url) {
    // La campaña que trajo al cliente a la landing viaja con él a renfeviajes. Sin esto,
    // cada compra que venga de una landing se atribuiría a "directo".
    window.location.href = aplicar(url, this.atribucion)
  }

  // --- un bloque de buscador ---------------------------------------------------------
  cablearBloque (bloque) {
    const estado = this.estadoDe(bloque)

    // Los dos campos de estación. OJO con la pestaña Ocio: su único campo arrastra en el
    // markup las clases `station2Input` y `buscadorDestinoOcioTren`, pero NO es un campo de
    // estación — lo lleva ocio.js con el catálogo. Si se cablea aquí también, el mismo campo
    // acaba con dos autocompletados encima peleándose por la lista.
    const esOcio = bloque === 'bloqueActividades'
    for (const [cual, selector] of (esOcio ? [] : Object.entries(CAMPOS))) {
      const campo = this.nodo(bloque, selector)
      if (!campo) continue
      const etiqueta = campo.closest('.rvb-form-label')
      const contenedor = campo.closest('.rvb-contenedor-estacion')
      const catalogo = this.catalogoDe(bloque, cual)

      const ac = autocompletar({
        campo,
        overlays: this.overlays,
        // Una letra como mínimo: `Search/stationsRenfe` con `q` vacío responde 400, así que
        // pedir al enfocar era una petición fallida por cada vez que el cliente toca el campo.
        // typeahead se configura con minLength 0 pero tampoco llega a consultar en vacío.
        minimo: 1,
        buscar: (q, señal) => catalogo.buscar(q, señal),
        // Lo que la caché sepa se enseña al instante; la respuesta del servidor llega detrás
        // y manda, así que no se pierde ninguna estación por el camino.
        adelanto: (q) => catalogo.adelanto(q),
        alResponder: (q, filas) => catalogo.recordar(q, filas),
        alElegir: (estacion) => {
          estado[cual] = estacion
          if (contenedor) contenedor.classList.remove('rvb-error')
          this.revisar(bloque)
        },
        alInvalidar: () => {
          // Teclear tira la estación elegida: si no, se podría buscar con un nombre a medio
          // escribir que el servidor nunca devolvió, y la URL de salida saldría sin id.
          estado[cual] = null
          this.revisar(bloque)
        },
      })
      this.piezas.push(ac)
      if (!this.campos) this.campos = {}
      this.campos[bloque + '.' + cual] = { ac, campo, contenedor, estado, cual, catalogo }

      // Los estados visuales que hoy pone el JS a mano.
      // El CONTENEDOR de la estación también se marca, y no es un detalle de más: en MÓVIL, de
      // esa clase cuelga la maqueta a pantalla completa --`position: fixed`, la lista ocupando
      // toda la pantalla y la flecha de volver-- que es como se comporta el buscador de siempre
      // por debajo de 768px. El original la pone en los dos sitios; poniéndola sólo en el
      // bloque, en el móvil salía un desplegable normal donde debía salir un panel.
      campo.addEventListener('focus', () => {
        if (etiqueta) etiqueta.classList.add('rvb-active')
        if (contenedor) contenedor.classList.add('rvb-open')
        this.raiz.querySelectorAll('.rvb-bloque-estaciones').forEach((b) => b.classList.add('rvb-open'))
        abrirPanelMovil(campo)
      })
      campo.addEventListener('blur', () => {
        if (etiqueta) etiqueta.classList.remove('rvb-active')
        if (contenedor) contenedor.classList.remove('rvb-open')
        cerrarPanelMovil(campo)
        // Un campo con texto pero sin estación elegida es un error: es exactamente el caso
        // que deja la URL de salida sin `id`.
        if (etiqueta) etiqueta.classList.toggle('rvb-error', !estado[cual] && campo.value.trim() !== '')
      })
    }

    // Un clic fuera del buscador cierra lo que hubiera abierto.
    const fuera = (e) => {
      if (this.raiz.contains(e.target) || this.overlays.contains(e.target)) return
      // Con `data-desplegado`, el bloque se queda abierto: lo que se cierra es el panel del
      // campo, no el buscador entero.
      const queCerrar = this.opciones.desplegado
        ? '.rvb-contenedor-estacion'
        : '.rvb-bloque-estaciones, .rvb-contenedor-estacion'
      this.raiz.querySelectorAll(queCerrar)
        .forEach((b) => b.classList.remove('rvb-open'))
      this.raiz.querySelectorAll('.rvb-menu-desplegable').forEach((m) => m.classList.add('rvb-oculto'))
    }
    document.addEventListener('mousedown', fuera)
    this.piezas.push({ destruir: () => document.removeEventListener('mousedown', fuera) })

    // El calendario y los pasajeros, cada uno en su fichero. Los dos devuelven null si el
    // bloque no los tiene: la pestaña Ocio, por ejemplo, no tiene ni fechas ni pasajeros.
    const calendario = crearCalendario(this, bloque)
    if (calendario) this.piezas.push(calendario)
    const pasajeros = crearPasajeros(this, bloque)
    if (pasajeros) {
      this.piezas.push(pasajeros)
      // El flujo de búsqueda necesita poder abrir el modal de pasajeros del bloque.
      this.pasajerosDe = this.pasajerosDe || {}
      this.pasajerosDe[bloque] = pasajeros
    }
    if (esOcio) {
      const ocio = crearOcio(this, bloque)
      if (ocio) { this.piezas.push(ocio); this.ocio = ocio }
    } else {
      // El botón de buscar y toda la conversación que lleva hasta la URL de compra.
      const busqueda = crearBusqueda(this, bloque)
      if (busqueda) {
        this.piezas.push(busqueda)
        this.busquedaDe = this.busquedaDe || {}
        this.busquedaDe[bloque] = busqueda
      }
    }

    this.revisar(bloque)
  }

  /**
   * Habilita o deshabilita el botón de buscar. En la home esto es `habilitarBtnBuscar()`, y
   * la condición es la misma: hacen falta las dos estaciones ELEGIDAS, no escritas.
   */
  revisar (bloque) {
    const estado = this.estadoDe(bloque)
    const boton = this.nodo(bloque, '.rvb-btn-buscar')
    if (!boton) return
    const listo = Boolean(estado.origen && estado.destino)
    boton.disabled = !listo
  }

  destruir () {
    this.piezas.forEach((p) => { try { p.destruir() } catch (e) { /* da igual */ } })
    if (this.dom) this.dom.destruir()
  }
}

/**
 * La forma corta, que es la que usa el snippet.
 * @returns {Buscador|null}
 */
export function montar (destino, opciones = {}) {
  const b = new Buscador(Object.assign({}, opciones, { destino }))
  return b.raiz ? b : null
}
